import React, { useState, useRef, useEffect } from "react";
import LogoCarousel from "../Campaigns/components/IntegrationSupport/IntegrationSupport";
import Footer from "../../components/Footer/Footer";
import VoiceAIForm from "../Campaigns/CustomUsecaseForm/CustomUsecaseForm";
import Navbar from "../../components/NavigationMenu/NavBar";
import { Button, Modal } from "@mui/material";
import { Mic } from "@mui/icons-material";
import useSEO from "../../hooks/useSEO";
import { useParams } from "react-router-dom";
import CalendarModal from "../../components/CalendarModal/CalendarModal";
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { CheckCircle, ArrowForward } from "@mui/icons-material";
import Vapi from "@vapi-ai/web";
import { voiceAgentService } from "../../services/VoiceAgentService";

const vapi = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY);

const handleVoiceToggle = async (
  isActive,
  setActive,
  agentId = process.env.REACT_APP_VAPI_BEANBAG_ASSISTANT_ID
) => {
  if (!isActive) {
    try {
      // Use the provided agentId or fall back to default
      vapi.start(agentId);
      setActive(true);
      console.log(
        "Voice interaction started successfully with agent:",
        agentId
      );
    } catch (error) {
      console.error("Error starting Vapi assistant:", error);
      alert(
        "Failed to start voice interaction. Please check your microphone permissions and try again."
      );
    }
  } else {
    try {
      vapi.stop();
      setActive(false);
      console.log("Voice interaction stopped");
    } catch (error) {
      console.error("Error stopping Vapi assistant:", error);
    }
  }
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomerAgent = () => {
  const { id } = useParams();

  const [modalOpen, setModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callInitiated, setCallInitiated] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState("");
  const [isPhoneCallLoading, setIsPhoneCallLoading] = useState(false);
  const [agentConfig, setAgentConfig] = useState(null);

  const getAgentId = () => {
    if (id) {
      // Use the ID from URL parameter
      return id;
    }
    // Fall back to default environment variable
    return process.env.REACT_APP_VAPI_BEANBAG_ASSISTANT_ID;
  };

  useEffect(() => {
    const loadAgentConfig = async () => {
      try {
        const currentAgentId = getAgentId();

        // You can create a mapping of IDs to configurations
        const agentConfigs = {
          "customer-support": {
            title: "Customer Support Agent",
            description:
              "AI-powered voice agents that handle customer inquiries 24/7 with human-like conversations.",
            useCaseId: "customer-support",
          },
          "sales-assistant": {
            title: "Sales Assistant Agent",
            description:
              "Intelligent voice agents that recover abandoned carts and guide customers to complete their purchases.",
            useCaseId: "sales-assistant",
          },
          "technical-support": {
            title: "Technical Support Agent",
            description:
              "Specialized voice agents for technical troubleshooting and IT support.",
            useCaseId: "technical-support",
          },
          "appointment-booking": {
            title: "Appointment Booking Agent",
            description:
              "Voice agents that handle appointment scheduling and calendar management.",
            useCaseId: "appointment-booking",
          },
        };

        // Set agent configuration based on ID or use default
        const config =
          agentConfigs[currentAgentId] || agentConfigs["customer-support"];
        setAgentConfig(config);

        console.log("Using agent ID:", currentAgentId);
        console.log("Agent configuration:", config);
      } catch (error) {
        console.error("Error loading agent configuration:", error);
        // Set default configuration
        setAgentConfig({
          title: "Customer Support Agent",
          description:
            "AI-powered voice agents that handle customer inquiries 24/7 with human-like conversations.",
          useCaseId: "customer-support",
        });
      }
    };

    loadAgentConfig();
  }, [id]);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    vapi.on("call-start", () => {
      setIsCallActive(true);
      setCallStatus("Voice interaction started");
    });

    vapi.on("call-end", () => {
      setIsCallActive(false);
      setCallStatus("Voice interaction ended");
      setIsVoiceActive(false);
    });

    vapi.on("error", (error) => {
      console.error("VAPI Error:", error);
      setCallStatus("Voice interaction failed");
      setIsCallActive(false);
      setIsVoiceActive(false);
    });

    return () => {
      vapi.removeAllListeners();
    };
  }, []);

  const handlePhoneCall = async (useCaseId = "customer-support") => {
    if (!phoneNumber) {
      alert("Please enter a phone number");
      return;
    }

    setIsPhoneCallLoading(true);
    setCallInitiated(false);

    try {
      const fullPhoneNumber = `${selectedCountry}${phoneNumber}`;
      const result = await voiceAgentService.makeOutboundCall(
        fullPhoneNumber,
        useCaseId
      );

      if (result.status === "started") {
        setCallInitiated(true);
        setCallStatus(`Outbound ${useCaseId} call initiated successfully`);
      } else {
        throw new Error("Call failed to start");
      }
    } catch (error) {
      console.error("Error making outbound call:", error);
      setCallStatus("Failed to initiate outbound call");
      alert("Failed to start outbound call. Please try again.");
    } finally {
      setIsPhoneCallLoading(false);
    }
  };

  const styles = {
    body: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: "1.6",
      color: "#333",
      background: "#ffffff",
      margin: 0,
      padding: 0,
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 0px",
    },
    header: {
      position: "fixed",
      top: 0,
      width: "100%",
      background: isScrolled
        ? "rgba(255, 255, 255, 0.98)"
        : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(10px)",
      zIndex: 1000,
      transition: "all 0.3s ease",
      boxShadow: isScrolled ? "0 2px 20px rgba(0, 0, 0, 0.1)" : "none",
    },
    nav: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    hero: {
      padding: "120px 0 80px",
      background:
        "radial-gradient(ellipse at top, rgba(226, 107, 74, 0.05) 0%, transparent 70%)",
    },
    heroTitle: {
      fontSize: "clamp(2.5rem, 5vw, 4rem)",
      fontWeight: "700",
      marginBottom: "24px",
      background: "linear-gradient(135deg, #333,rgb(226, 104, 74))",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      animation: "fadeInUp 1s ease-out",
    },
    heroText: {
      fontSize: "1.25rem",
      maxWidth: "800px",
      margin: "0 auto 40px",
      color: "#666",
      animation: "fadeInUp 1s ease-out 0.2s both",
    },
    voiceAgentSection: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "250px",
    },
    voiceAgentContainer: {
      marginBottom: "48px",
      marginTop: "32px",
    },
    voiceAgentCircle: {
      position: "relative",
      margin: "0 auto",
      width: "192px",
      height: "192px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #f09481, #ea6347)",
      padding: "4px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      boxShadow: "0 4px 20px rgba(226, 107, 74, 0.3)",
    },
    voiceAgentCircleActive: {
      animation: "pulse 2s infinite",
      boxShadow: "0 8px 40px rgba(226, 107, 74, 0.5)",
      transform: "scale(1.05)",
    },
    voiceAgentInner: {
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      background: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid rgba(0, 0, 0, 0.1)",
    },
    voiceAgentIconContainer: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    featuresGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gridTemplateRows: "repeat(2, 1fr)",
      gap: "24px",
      marginTop: "32px",
    },
    voiceAgentIcon: {
      fontSize: "80px",
      color: "#ea6347",
      zIndex: 2,
    },
    voiceAgentPulse: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "80px",
      height: "80px",
      border: "4px solid #ea6347",
      borderRadius: "50%",
      animation: "ping 2s infinite",
    },
    voiceAgentText: {
      color: "#666",
      marginTop: "24px",
      fontSize: "1.3rem",
      fontWeight: "500",
    },
    clientLogos: {
      padding: "60px 0",
      background: "#f8f9fa",
      overflow: "hidden",
    },
    section: {
      padding: "80px 0",
    },
    sectionAlt: {
      padding: "40px 0",
      background: "#f8f9fa",
    },
    sectionTitle: {
      fontSize: "2.5rem",
      fontWeight: "700",
      textAlign: "center",
      marginBottom: "40px",
      color: "#333",
      margin: "10px",
    },
    voiceSection: {
      padding: "80px 0",
      textAlign: "center",
    },
    voiceContent: {
      maxWidth: "600px",
      margin: "0 auto",
      padding: "0 10px",
    },
    benefitsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "20px",
      marginTop: "20px",
      marginLeft: "40px",
      marginRight: "40px",
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "16px",
      background: "#ffffff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
      transition: "all 0.3s ease",
    },
    benefit: {
      textAlign: "center",
      padding: "40px 30px",
      background: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.08)",
      height: "13rem",
    },
    benefitNumber: {
      fontSize: "3rem",
      fontWeight: "700",
      color: "#ea6347",
      display: "block",
      marginBottom: "10px",
    },
    testimonial: {
      padding: "80px 0",
      textAlign: "center",
    },
    testimonialContent: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "60px 40px",
      background: "white",
      borderRadius: "20px",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
      position: "relative",
    },
    testimonialText: {
      fontSize: "1rem",
      lineHeight: "1.6",
      marginBottom: "1.5rem",
      fontStyle: "italic",
      color: "#333",
    },
    testimonialAuthor: {
      fontWeight: "600",
      color: "#ea6347",
    },
    security: {
      padding: "80px 0",
      background: "#f8f9fa",
      textAlign: "center",
    },
    securityBadges: {
      display: "flex",
      justifyContent: "center",
      gap: "40px",
      marginTop: "40px",
      flexWrap: "wrap",
    },
    badge: {
      padding: "15px 30px",
      background: "white",
      borderRadius: "50px",
      fontWeight: "600",
      color: "#ea6347",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
    },
    featureTitle: {
      fontSize: "1.25rem",
      fontWeight: "600",
      marginBottom: "10px",
      color: "#333",
    },
    featureText: {
      color: "#666",
    },
    testimonialCard: {
      backgroundColor: "#ffffff",
      padding: "2rem",
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      minWidth: "300px",
      flex: "1",
      border: "1px solid #e0e0e0",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      margin: "10px",
    },
    testimonialRating: {
      fontSize: "1.2rem",
      color: "#ffc107",
      marginBottom: "1rem",
    },
    testimonialsRow: {
      display: "flex",
      gap: "2rem",
      marginTop: "2rem",
      overflowX: "auto",
      padding: "1rem 0",
    },
  };

  const useCases = [
    {
      id: "sales-assistant",
      title: "Cart Recovery Agent (Shopify)",
      description:
        "Intelligent voice agents that recover abandoned carts and guide customers to complete their purchases.",
      features: [
        "60% Recovery Rate",
        "100% Automation",
        "Complete Domain Understanding",
        "Call Transcripts & metrics",
        "Easy Integrations",
        "Multilingual support",
      ],
      testUrl: "/test/sales-assistant",
      showNote: true,
    },
    {
      id: "customer-support",
      title: "AI Customer Support",
      description:
        "AI-powered voice agents that handle customer inquiries 24/7 with human-like conversations.",
      features: [
        "Select from top-notch voices",
        "Upload your own Knowledge",
        "API & MCP Integration for Real-time Query Resolution",
        "Call analytics & Recordings",
        "24/7 Availability",
        "Multi-language support",
      ],
      testUrl: "/test/customer-support",
      showNote: false,
    },
    {
      id: "technical-support",
      title: "Technical Support",
      description:
        "Specialized voice agents for technical troubleshooting and IT support.",
      features: [
        "Technical Diagnostics",
        "Step-by-step Guidance",
        "Knowledge Base Access",
        "Ticket Creation",
      ],
      testUrl: "/test/technical-support",
      showNote: false,
    },
    {
      id: "appointment-booking",
      title: "Appointment Booking",
      description:
        "Voice agents that handle appointment scheduling and calendar management.",
      features: [
        "Calendar Integration",
        "Availability Checking",
        "Reminder Notifications",
        "Rescheduling",
      ],
      testUrl: "/test/appointment-booking",
      showNote: false,
    },
  ];

  const features = [
    {
      icon: "⚡",
      title: "Efficiency",
      description: "Get results in hours, not weeks.",
    },
    {
      icon: "👤",
      title: "Personalization",
      description:
        "Engage with customers through personalized voice interactions.",
    },
    {
      icon: "📈",
      title: "Scalability",
      description: "Reach millions of users with ease.",
    },
    {
      icon: "🔒",
      title: "Security",
      description:
        "Your data is safe with us. We comply with the highest standards.",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "CTO, TechCorp",
      content:
        "Beanbag voice agents reduced our support costs by 60% while improving customer satisfaction.",
      rating: 5,
    },
    {
      name: "Michael Chen",
      role: "Head of Operations, StartupXYZ",
      content:
        "The implementation was seamless and the results were immediate. Highly recommended!",
      rating: 5,
    },
    {
      name: "Emily Rodriguez",
      role: "Customer Success Manager, Enterprise Inc",
      content:
        "Our customers love the natural conversations. It feels like talking to a real person.",
      rating: 5,
    },
  ];

  const benefits = [
    {
      number: "3x",
      title: "Longer Responses",
      description:
        "Customers provide more detailed feedback compared to traditional methods.",
    },
    {
      number: "<24h",
      title: "Fast Results",
      description: "Get actionable insights within hours.",
    },
    {
      number: "+30M",
      title: "Possible Respondents",
      description: "Reach a broad audience with ease.",
    },
  ];

  useSEO({
    title: `Voice - Beanbag AI`,
    description: "Voice Description",
    canonical: "https://www.beanbag.ai/voice",
    keywords:
      "AI voice agents, voice AI, customer support automation, voice technology",
    ogTitle: "Voice AI Solutions - Beanbag AI",
    ogDescription: "Unlock deeper customer insights with our voice agents",
    ogImage: "https://www.beanbag.ai/images/beanbag-social-card.jpg",
    ogUrl: "https://www.beanbag.ai/voice",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Beanbag AI",
      url: "https://www.beanbag.ai",
      logo: "https://www.beanbag.ai/logo192.png",
      description:
        "Beanbag AI provides intelligent voice automation solutions to enhance customer interactions.",
    },
  });

  const renderPhoneCallSection = (useCaseId) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
          maxWidth: "400px",
        }}
      >
        <FormControl sx={{ minWidth: "80px" }}>
          <Select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            size="small"
          >
            <MenuItem value="+1">🇺🇸 +1</MenuItem>
            <MenuItem value="+44">🇬🇧 +44</MenuItem>
            <MenuItem value="+91">🇮🇳 +91</MenuItem>
            <MenuItem value="+966">🇸🇦 +966</MenuItem>
            <MenuItem value="+86">🇨🇳 +86</MenuItem>
            <MenuItem value="+49">🇩🇪 +49</MenuItem>
            <MenuItem value="+33">🇫🇷 +33</MenuItem>
            <MenuItem value="+81">🇯🇵 +81</MenuItem>
            <MenuItem value="+61">🇦🇺 +61</MenuItem>
            <MenuItem value="+55">🇧🇷 +55</MenuItem>
            <MenuItem value="+7">🇷🇺 +7</MenuItem>
          </Select>
        </FormControl>
        <TextField
          type="tel"
          placeholder="Enter phone number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          size="small"
          sx={{ flexGrow: 1 }}
        />
      </Box>

      <Button
        variant="contained"
        size="large"
        endIcon={<ArrowForward />}
        onClick={() => handlePhoneCall(useCaseId)}
        disabled={isPhoneCallLoading || !phoneNumber}
        sx={{
          backgroundColor: "#ea6347",
          "&:hover": { backgroundColor: "#d55640" },
          padding: "12px 24px",
          width: "100%",
          maxWidth: "400px",
        }}
      >
        {isPhoneCallLoading ? "Calling..." : "Talk Now"}
      </Button>

      {callInitiated && (
        <Typography
          sx={{
            color: "#4caf50",
            fontSize: "0.875rem",
            textAlign: "center",
            marginTop: "8px",
            fontWeight: "500",
          }}
        >
          You should be receiving a call right now.
        </Typography>
      )}

      <Typography
        sx={{
          color: "#666",
          fontSize: "0.875rem",
          textAlign: "center",
          maxWidth: "400px",
        }}
      >
        Don't wait for the demo. Just enter your number and receive a call from
        our agent now.
      </Typography>
    </Box>
  );

  return (
    <div style={styles.body}>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 4px 20px rgba(226, 107, 74, 0.3);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 8px 40px rgba(226, 107, 74, 0.5);
            }
          }

          @keyframes ping {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            75%, 100% {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
          }

          @media (max-width: 768px) {
            .section-title {
              font-size: 2rem !important;
            }
            .hero-title {
              font-size: 2.5rem !important;
            }
            .hero-text {
              font-size: 1.1rem !important;
            }
            .benefits-grid {
              grid-template-columns: 1fr !important;
            }
            .hero-container {
              flex-direction: column !important;
              align-items: center;
              gap: 2rem;
            }
            .hero-content {
              max-width: 100% !important;
              text-align: center;
            }
            .voice-agent-section {
              min-width: unset !important;
            }
          }
        `}
      </style>

      <header style={styles.header}>
        <div style={styles.container}>
          <nav style={styles.nav}>
            <Navbar />
          </nav>
        </div>
      </header>

      <main>
        <section style={styles.hero}>
          <div
            style={{
              ...styles.container,
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
              gap: "12rem",
            }}
            className="hero-container"
          >
            <div
              style={{ flex: 1, maxWidth: "600px" }}
              className="hero-content"
            >
              <h1 style={styles.heroTitle} className="hero-title">
                Unlock deeper customer insights with our voice agents
              </h1>
              <p style={styles.heroText} className="hero-text">
                It is more than just an AI voice agent—it replaces traditional
                methods with intelligent, voice-moderated interactions. Our AI
                voice agent conducts personalized conversations at scale,
                providing you with deep insights fast.
              </p>
              <Button
                variant="text"
                onClick={handleOpenModal}
                sx={{
                  textDecoration: "underline",
                  textTransform: "none",
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  fontWeight: 500,
                  p: "8px 16px",
                  borderRadius: "8px",
                  position: "relative",
                  transition: "all 0.3s ease",
                  backgroundColor: "rgba(234, 99, 71, 0.1)",
                  color: "#ea6347",
                  textDecoration: "none",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Book a Demo
              </Button>
            </div>

            <div
              style={styles.voiceAgentSection}
              className="voice-agent-section"
            >
              <div
                style={{
                  ...styles.voiceAgentCircle,
                  ...(isVoiceActive ? styles.voiceAgentCircleActive : {}),
                }}
                className="voice-agent-circle"
                onClick={() =>
                  handleVoiceToggle(
                    isVoiceActive,
                    setIsVoiceActive,
                    getAgentId()
                  )
                }
              >
                <div style={styles.voiceAgentInner}>
                  <div style={styles.voiceAgentIconContainer}>
                    <Mic style={styles.voiceAgentIcon} />
                    {isVoiceActive && (
                      <div style={styles.voiceAgentPulse}></div>
                    )}
                  </div>
                </div>
              </div>
              <p style={styles.voiceAgentText}>
                {isVoiceActive ? "Click to Stop" : "Talk to your Agent"}
              </p>
            </div>
          </div>
        </section>

        <section style={styles.clientLogos}>
          <LogoCarousel />
        </section>

        <section style={styles.section}>
          <div style={styles.container}>
            <div style={{ textAlign: "center", marginBottom: "64px" }}>
              <Typography
                variant="h4"
                component="h3"
                sx={{
                  marginBottom: "16px",
                  color: "#333",
                  fontWeight: "700",
                }}
              >
                AI Agent that works for you 24/7
              </Typography>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: "#666",
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                Experience how our voice agent interacts with your customers.
              </p>
            </div>

            <Card
              sx={{
                backgroundColor: "white",
                boxShadow: "0 3px 12px rgba(0, 0, 0, 0.06)",
                borderRadius: "16px",
                maxWidth: "1000px",
                margin: "0 auto",
                padding: "32px",
              }}
            >
              <Typography
                variant="h5"
                component="h4"
                sx={{
                  marginBottom: "24px",
                  color: "#333",
                  fontWeight: "600",
                  textAlign: "center",
                }}
              >
                Key Features
              </Typography>

              <div style={styles.featuresGrid} className="features-grid">
                {[
                  "24/7 Support",
                  "100% Automation",
                  "Multilingual support",
                  "Call Transcripts & metrics",
                  "Complete Domain Understanding",
                  "Easy Integrations",
                ].map((feature, index) => (
                  <div
                    key={index}
                    style={styles.featureItem}
                    className="feature-item"
                  >
                    <CheckCircle
                      sx={{
                        color: "#ea6347",
                        fontSize: "20px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontWeight: "500",
                        fontSize: "0.9rem",
                      }}
                    >
                      {feature}
                    </Typography>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        <section style={styles.sectionAlt}>
          <div style={styles.container}>
            <div
              style={{
                textAlign: "center",
                marginBottom: "64px",
                padding: "10px",
              }}
            >
              <h2 style={styles.sectionTitle} className="section-title">
                Why Choose Beanbag AI?
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: "#666",
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                Built for enterprise scale with cutting-edge AI technology
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "24px",
                marginTop: "40px",
              }}
            >
              {features.map((feature, index) => (
                <Card
                  key={index}
                  sx={{
                    margin: "10px",
                    backgroundColor: "white",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                    boxShadow: "0 3px 12px rgba(0, 0, 0, 0.06)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "rgba(234, 99, 71, 0.5)",
                      boxShadow: "0 8px 25px rgba(234, 99, 71, 0.15)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <CardContent sx={{ padding: "24px" }}>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        background: "linear-gradient(135deg, #f09481, #ea6347)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <div style={{ color: "white", fontSize: "24px" }}>
                        {feature.icon}
                      </div>
                    </div>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: "600",
                        marginBottom: "8px",
                        color: "#333",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        lineHeight: "1.6",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section style={styles.voiceSection}>
          <div style={styles.container}>
            <div style={styles.voiceContent}>
              <h2 style={styles.sectionTitle} className="section-title">
                More Than Just Words
              </h2>
              <p style={{ ...styles.heroText, animation: "none" }}>
                Voice interactions capture the nuances of human
                communication—tone, inflection, and emotion. Beanbag AI goes
                beyond text to uncover deeper user sentiment, providing you with
                richer insights.
              </p>
            </div>
          </div>
        </section>

        <section style={styles.sectionAlt}>
          <div style={styles.container}>
            <h2 style={styles.sectionTitle} className="section-title">
              Key Benefits
            </h2>
            <div style={styles.benefitsGrid} className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div key={index} style={styles.benefit}>
                  <span style={styles.benefitNumber}>{benefit.number}</span>
                  <h3 style={styles.featureTitle}>{benefit.title}</h3>
                  <p style={styles.featureText}>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={styles.security}>
          <div style={styles.container}>
            <h2 style={styles.sectionTitle} className="section-title">
              Your Data is Safe with Us
            </h2>
            <p style={styles.featureText}>
              We prioritize your data security. Beanbag AI is compliant with
              industry standards.
            </p>
            <div style={styles.securityBadges}>
              <div style={styles.badge}>SOC 2 Type II</div>
              <div style={styles.badge}>GDPR Compliant</div>
            </div>
          </div>
        </section>
      </main>

      <div id="contact" style={{ padding: "20px" }}>
        <Footer />
      </div>

      <CalendarModal open={modalOpen} handleClose={handleCloseModal} />
    </div>
  );
};

export default CustomerAgent;
